import type {
  ApplicationAddressApi,
  ApplicationAddressRow,
  ApplicationIndividual,
  ApplicationIndividualRole,
  ApplicationCommercialEntity,
  ApplicationCommercialEntityRow,
  ApplicationCommercialEntityWrite,
  ApplicationConsumerData,
  ApplicationEntityRelationship,
  ApplicationEntityRoleRow,
  ApplicationIndividualRoleRow,
  ApplicationIndividualRow,
  ApplicationIndividualView,
  ApplicationIndividualWrite,
  ApplicationPartiesSnapshot,
  ApplicationPartiesWrite,
  ApplicationPartiesView,
  ApplicationPartyRoleView,
  ApplicationIndividualRoleFlags,
  JsonObject,
  JsonValue,
} from '../domain/application-parties.domain';
import type { RoleTypes, SignerRoleTypes } from '../domain/application-service.domain';
import type { BusinessNumberSearchValue } from '../domain/component/business-number-search-component.domain';
import { entityTypeFromCode } from './abr';
import { commercialEntityRoleType } from './application-party-labels';
import { compareIdentity, pairIndividualsByIdentity } from './individual-identity';

export const isJsonObject = (value: unknown): value is JsonObject => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

export const isJsonValue = (value: unknown): value is JsonValue => {
  if (value === null) return true;
  if (['string', 'number', 'boolean'].includes(typeof value)) return true;
  if (Array.isArray(value)) return value.every(isJsonValue);
  if (isJsonObject(value)) return Object.values(value).every(v => v === undefined || isJsonValue(v));
  return false;
};

export interface ApiJsonToApplicationPartiesOptions {
  generateIndividualId?: () => string;
  currentParties?: ApplicationPartiesSnapshot;
  // Identifies stored individuals this payload does not describe (the KYB-owned rows). They are held
  // out of the positional match below, so an individual the payload adds is inserted rather than
  // written over one of them.
  isIndividualOwnedOutsidePayload?: (row: ApplicationIndividualRow) => boolean;
}

/**
 * Payload to rows: which stored row each incoming party describes, and what that row becomes. The one
 * place that decides whether a save adds, updates or soft-deletes, so it is also the one place a
 * mismatched shape can silently blank a column.
 */
export const applicationPartiesToRows = (
  applicationId: number,
  parties: ApplicationPartiesWrite,
  options: ApiJsonToApplicationPartiesOptions = {},
): ApplicationPartiesSnapshot => {
  // An array the payload omits is not described by this save, so its stored rows carry through as
  // they are. Deliberately not rebuilt from a projection of themselves: this shape cannot express
  // every stored role - a shareholding held against the corporate trustee has nowhere to sit - so a
  // rebuild drops whatever it has no vocabulary for, and the full sync downstream reads that
  // absence as a deletion.
  //
  // Copied because additionalRolesToRows marks a matched entity as a guarantor in place. Reusing
  // the caller's rows directly would let a payload that never mentioned an entity relabel it.
  const commercialEntityRows = parties.commercialEntities
    ? parties.commercialEntities.map((entity, index) =>
        apiCommercialEntityToRow(applicationId, entity, index, options.currentParties))
    : (options.currentParties?.commercialEntities ?? []).map(row => ({ ...row }));
  const entityRoles = parties.commercialEntities
    ? apiCommercialEntityRolesToRows(applicationId, parties.commercialEntities, commercialEntityRows)
    : (options.currentParties?.entityRoles ?? []);
  const currentIndividuals = options.currentParties?.individuals ?? [];
  const incomingIndividuals = parties.individuals;
  if (!incomingIndividuals) {
    return {
      applicationId,
      individuals: currentIndividuals,
      commercialEntities: commercialEntityRows,
      individualRoles: options.currentParties?.individualRoles ?? [],
      entityRoles,
    };
  }
  const matchedRows = matchIndividualsToRows(
    incomingIndividuals,
    currentIndividuals,
    options.isIndividualOwnedOutsidePayload ?? (() => false),
  );
  const claimIndividualSortOrder = sortOrderAllocator(currentIndividuals);
  const individualRows = incomingIndividuals.map((individual, index) => {
    const current = matchedRows[index];
    // A matched row keeps its place; only a genuinely new individual takes a free sort order, so it
    // cannot land on one already held by a row the payload does not own.
    return apiIndividualToRow(
      applicationId,
      individual,
      current?.sortOrder ?? claimIndividualSortOrder(),
      current,
      options.generateIndividualId,
    );
  });
  const borrower = commercialEntityRows.find(row => row.isPrimary) ?? commercialEntityRows[0];
  const primaryRoleRows = incomingIndividuals
    .map((individual, index) =>
      primaryIndividualRoleToRow(applicationId, individualRows[index], individual, borrower, index),
    )
    .filter((row): row is ApplicationIndividualRoleRow => row !== null);
  const additionalRoleRows = incomingIndividuals.flatMap((individual, individualIndex) =>
    additionalRolesToRows(
      applicationId,
      individualRows[individualIndex],
      individual.additionalRoles ?? [],
      commercialEntityRows,
      options.currentParties?.individualRoles ?? [],
      primaryRoleRows.length + individualIndex,
    ),
  );

  return {
    applicationId,
    individuals: individualRows,
    commercialEntities: commercialEntityRows,
    individualRoles: [...primaryRoleRows, ...additionalRoleRows],
    entityRoles,
  };
};

/**
 * The borrower entity, or null. An application legitimately may not have one: consumer applications
 * carry no entities at all, and KYB-added entities are always isPrimary false, so an application can
 * hold entities without a primary. No positional fallback - `commercialEntities[0]` is ordered by the
 * position the frontend sent, so guessing there picks an arbitrary company.
 *
 * Note this does not filter soft-deleted rows, matching every caller it replaces. The legacy
 * projection does filter them, so the two can disagree about a soft-deleted primary entity.
 */
export const primaryEntityRow = (
  snapshot: Pick<ApplicationPartiesSnapshot, 'commercialEntities'>,
): ApplicationCommercialEntityRow | null =>
  snapshot.commercialEntities.find(row => row.isPrimary) ?? null;

export const applicationIndividualView = (
  individual: ApplicationIndividualRow,
  individualRoles: ApplicationIndividualRoleRow[],
  commercialEntities: ApplicationCommercialEntityRow[],
  primaryEntity: ApplicationCommercialEntityRow | null,
): ApplicationIndividualView => {
  const roleRows = individualRoles
    .filter(role => !role.isDeleted && roleBelongsToIndividual(role, individual))
    .sort((a, b) => roleOrderKey(a) - roleOrderKey(b));
  const primaryRoleRow = roleRows.find(role =>
    (!!primaryEntity?.id && role.commercialEntityId === primaryEntity.id) ||
    (!!primaryEntity?.matchKey && role.commercialEntityMatchKey === primaryEntity.matchKey)
  ) ?? roleRows.find(role => !role.commercialEntityId && !role.commercialEntityMatchKey);
  const toView = (role: ApplicationIndividualRoleRow): ApplicationPartyRoleView => ({
    role,
    entity: findCommercialEntityByRole(commercialEntities, role) ?? null,
  });
  return {
    individual,
    primaryRole: primaryRoleRow ? toView(primaryRoleRow) : null,
    additionalRoles: roleRows
      .filter(role => role !== primaryRoleRow)
      .filter(role => !!role.commercialEntityId || !!role.commercialEntityMatchKey)
      .map(toView),
  };
};

export const applicationPartiesView = (
  snapshot: Pick<ApplicationPartiesSnapshot, 'individuals' | 'individualRoles' | 'commercialEntities'>,
): ApplicationPartiesView => {
  const primaryEntity = primaryEntityRow(snapshot);
  return {
    primaryEntity,
    individuals: [...snapshot.individuals]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map(individual =>
        applicationIndividualView(individual, snapshot.individualRoles, snapshot.commercialEntities, primaryEntity)),
  };
};

const addressRowToApiAddress = (address: ApplicationAddressRow): ApplicationAddressApi => {
  const { id, applicationId, source, createdAt, lastUpdate, ...rest } = address;
  return rest;
};

// The union of the two sources, because neither is sufficient alone. The role flags miss a
// trustee/partner on an application with no primary entity: both apiCommercialEntityRolesToRows
// and the 5.8.0-05 backfill require an owner to point at, so no edge is written and the entity
// looks role-less despite its Type having stored isBorrower = 1. The column in turn is a stale 0
// on rows saved before it existed, which is why the guarantor-coverage credit rule ORs in
// isPrimary. Taking either as gospel loses real borrowers; the guarantor guard applies to both.
const entityRowIsBorrower = (
  row: ApplicationCommercialEntityRow,
  entityRoles: ApplicationEntityRoleRow[],
): boolean => {
  const live = liveEntityRolesFor(row, entityRoles);
  return isBorrowerEntityFlags({
    isPrimary: row.isPrimary,
    isGuarantor: row.isGuarantor,
    isTrustee: live.some(role => role.isTrustee),
    isPartner: live.some(role => role.isPartner),
  }) || (!row.isGuarantor && !!row.isBorrower);
};

const roleViewToApi = (
  { role, entity }: ApplicationPartyRoleView,
  isPrimaryRole: boolean,
  entityRoles: ApplicationEntityRoleRow[],
): ApplicationIndividualRole => ({
  entityId: entity?.id ?? null,
  entityLegalName: entity?.legalName ?? null,
  entityTradingName: entity?.tradingName ?? null,
  entityAbn: entity?.abn ?? null,
  entityAcn: entity?.acn ?? null,
  entityIsGuarantor: !!entity?.isGuarantor,
  // Same union as the entity's own isBorrower in applicationCommercialEntityToApi, so the two agree.
  entityIsBorrower: !!entity && entityRowIsBorrower(entity, entityRoles),
  isDirector: role.isDirector,
  isPartner: role.isPartner,
  isShareholder: role.isShareholder,
  isSoleTrader: role.isSoleTrader,
  isTrustee: role.isTrustee,
  isSpouse: role.isSpouse,
  isSigner: role.isSigner,
  isPrimaryRole,
});

/**
 * Projects one individual into the API read shape. Unlike the legacy projection this keeps every
 * role flag on every relationship, so an individual who is both a director and a shareholder of the
 * same entity survives the trip.
 */
export const applicationIndividualToApi = (
  view: ApplicationIndividualView,
  entityRoles: ApplicationEntityRoleRow[],
): ApplicationIndividual => {
  const row = view.individual;
  const consumer = parseConsumerData(row.consumerData);
  const roles = [
    ...(view.primaryRole ? [roleViewToApi(view.primaryRole, true, entityRoles)] : []),
    ...view.additionalRoles.map(roleView => roleViewToApi(roleView, false, entityRoles)),
  ];
  return {
    // id is optional on the row type only so inserts can omit it; a loaded row always carries it.
    id: row.id!,
    guid: row.legacyId,
    sfContactId: row.sfContactId ?? null,
    title: row.title ?? null,
    givenName: row.givenName ?? null,
    middleName: row.middleName ?? null,
    surname: row.surname ?? null,
    dateOfBirth: normalizeDate(row.dateOfBirth) ?? null,
    gender: row.gender ?? null,
    email: row.email ?? null,
    mobileNumber: row.mobileNumber ?? null,
    hasAlias: row.hasAlias ?? null,
    aliasNames: row.aliasNames ?? null,
    countryOfCitizenship: row.countryOfCitizenship ?? null,
    identification: row.identificationType || row.identificationNumber
      ? { type: row.identificationType ?? null, number: row.identificationNumber ?? null }
      : null,
    driverLicence: row.driverLicenseState || row.driverLicenseExpiry
      ? { state: row.driverLicenseState ?? null, expiry: normalizeDate(row.driverLicenseExpiry) ?? null }
      : null,
    homeAddress: row.homeAddress ? addressRowToApiAddress(row.homeAddress) : null,
    previousAddress: row.previousAddress ? addressRowToApiAddress(row.previousAddress) : null,
    isApplicant: !!row.isApplicant,
    isPrimaryContact: !!row.isPrimaryContact,
    isGuarantor: !!row.isGuarantor,
    isDeleted: !!row.isDeleted,
    roles,
    property: {
      isOwner: !!row.propertyOwnerFlag,
      value: row.propertyValue ?? null,
      mortgageBalance: row.mortgageBalance ?? null,
      address: row.propertyOwnedAddress ? addressRowToApiAddress(row.propertyOwnedAddress) : null,
    },
    employment: consumer,
    source: row.source ?? null,
    needsAttention: !!row.needsAttention,
    detailsSaved: !!row.detailsSavedAt,
    // mssql bit may arrive as true or 1; never write this back from the API payload.
    legacyPrivacyConsent: !!row.legacyPrivacyConsent,
  };
};

export const applicationIndividualsToApi = (
  snapshot: Pick<ApplicationPartiesSnapshot, 'individuals' | 'individualRoles' | 'commercialEntities' | 'entityRoles'>,
): ApplicationIndividual[] => applicationPartiesView(snapshot).individuals
  // Matches attachApplicationParties, which filters soft-deleted individuals at the read boundary,
  // and applicationCommercialEntitiesToApi, which already filters here. Without this the endpoint
  // returned removed people that the legacy application read hides, so consumers that find() an
  // applicant could resolve to one a broker had deleted. The filter is not in applicationPartiesView
  // because the legacy round-trip and mirror read that view and must retain deleted rows.
  .filter(view => !view.individual.isDeleted)
  .map(view => applicationIndividualToApi(view, snapshot.entityRoles));

/**
 * Projects one entity into the API read shape. Unlike the legacy projection this does not collapse the
 * entity to a single Type string, so a stored 'Member' or 'Secondary' is no longer reported as a
 * guarantor.
 */
export const applicationCommercialEntityToApi = (
  row: ApplicationCommercialEntityRow,
  entityRoles: ApplicationEntityRoleRow[],
  commercialEntities: ApplicationCommercialEntityRow[],
): ApplicationCommercialEntity => {
  const relationships: ApplicationEntityRelationship[] = liveEntityRolesFor(row, entityRoles).map(role => {
    const owner = findCommercialEntityById(commercialEntities, role.ownerEntityId) ??
      (role.ownerEntityMatchKey
        ? commercialEntities.find(item => item.matchKey === role.ownerEntityMatchKey)
        : undefined);
    return {
      ownerEntityId: owner?.id ?? role.ownerEntityId ?? null,
      ownerEntityLegalName: owner?.legalName ?? null,
      isTrustee: !!role.isTrustee,
      isPartner: !!role.isPartner,
    };
  });
  return {
    id: row.id!,
    legalName: row.legalName ?? null,
    tradingName: row.tradingName ?? null,
    abn: row.abn ?? null,
    acn: row.acn ?? null,
    entityType: row.entityType ?? null,
    isPrimary: !!row.isPrimary,
    isGuarantor: !!row.isGuarantor,
    isBorrower: entityRowIsBorrower(row, entityRoles),
    isDeleted: !!row.isDeleted,
    relationships,
    principalPlaceOfBusiness: row.principalPlaceOfBusinessAddress
      ? addressRowToApiAddress(row.principalPlaceOfBusinessAddress)
      : null,
    registeredBusinessAddress: row.registeredBusinessAddress
      ? addressRowToApiAddress(row.registeredBusinessAddress)
      : null,
    phoneNumber: row.phoneNumber ?? null,
    areaCode: row.areaCode ?? null,
    revenue: row.revenue ?? null,
    abnAgeMonths: row.abnAgeMonths ?? null,
    operatesAtCommercialAddress: row.operateAtCommercialAddressFlag ?? null,
    businessCategory: row.businessCategory ?? null,
    industry: row.industryDetails ?? null,
    industryClassCode: row.industryClassCode ?? null,
    source: row.source ?? null,
    manualEntry: row.manualEntry ?? null,
  };
};

/**
 * Live entities only, ordered by sortOrder so the array order matches the legacy one. sortOrder itself
 * is not published - the ordering is the contract, not the number.
 */
export const applicationCommercialEntitiesToApi = (
  snapshot: Pick<ApplicationPartiesSnapshot, 'commercialEntities' | 'entityRoles'>,
): ApplicationCommercialEntity[] => [...snapshot.commercialEntities]
  .filter(row => !row.isDeleted)
  .sort((a, b) => a.sortOrder - b.sortOrder)
  .map(row => applicationCommercialEntityToApi(row, snapshot.entityRoles, snapshot.commercialEntities));

/**
 * An entity as read, ready to be written back. For read-modify-write callers - a Salesforce inbound
 * sync, say - that need to echo an entity they did not fully construct.
 *
 * Drops only what the write shape refuses: isBorrower and source are server-derived, and
 * relationships narrow to the two flags a legacy Type string can carry plus the owner they point at,
 * without which every edge would be re-pointed at the borrower. Everything else crosses
 * intact, industryClassCode included, which is what stops a partial write-back blanking the column
 * the industry read path joins on.
 */
export const applicationCommercialEntityToWrite = (
  entity: ApplicationCommercialEntity,
): ApplicationCommercialEntityWrite => {
  const { isBorrower, relationships, source, ...rest } = entity;
  return {
    ...rest,
    relationships: relationships.map(({ isTrustee, isPartner, ownerEntityId }) => ({ isTrustee, isPartner, ownerEntityId })),
  };
};

/**
 * The party arrays for a save.
 *
 * The write shape rather than the legacy one, so the save is typed by what it actually persists. It
 * used to return readonly object[] and let the save cast, which let any caller hand it a mismatched
 * shape: it would compile, and every column the shape did not carry would be blanked.
 */
export const applicationPartiesSaveInput = (data: {
  parties?: ApplicationPartiesWrite;
}): ApplicationPartiesWrite => ({
  individuals: data.parties?.individuals,
  commercialEntities: data.parties?.commercialEntities,
});

export const roleFlagsFromApiRole = (role: RoleTypes): ApplicationIndividualRoleFlags => {
  return {
    isDirector: role === 'Director',
    isPartner: role === 'Partner',
    isShareholder: role === 'Shareholder',
    isSoleTrader: role === 'SoleTrader',
    isTrustee: role === 'Trustee',
    isSpouse: role === 'Spouse',
  };
};

export const hasAnyRoleFlag = (flags: ApplicationIndividualRoleFlags): boolean =>
  flags.isDirector || flags.isPartner || flags.isShareholder ||
  flags.isSoleTrader || flags.isTrustee || flags.isSpouse;

// applicant and guarantor are no longer role flags; they live at the individual level and are
// passed in to be slotted into the precedence position their old role flag held.
export const apiRoleFromRoleFlags = (
  row: ApplicationIndividualRoleFlags,
  opts: { isApplicant?: boolean; isGuarantor?: boolean } = {},
): RoleTypes => {
  if (opts.isApplicant) return 'Applicant';
  if (row.isDirector) return 'Director';
  if (row.isPartner) return 'Partner';
  if (row.isShareholder) return 'Shareholder';
  if (row.isSoleTrader) return 'SoleTrader';
  if (row.isTrustee) return 'Trustee';
  if (row.isSpouse) return 'Spouse';
  // Guarantor is the lowest-precedence Role: it is already conveyed via SignerRole
  // (Guarantor/GuarantorSigner), so a substantive role (SoleTrader/Partner/Trustee/etc.)
  // should win and Guarantor only surfaces as Role when no other role flag is set.
  if (opts.isGuarantor) return 'Guarantor';
  return null;
};

/**
 * ApplicationIndividual/ApplicationCommercialEntity.source marking a party the KYB save owns: a
 * stakeholder returned by a FrankieOne company check, or a partner/trustee typed in on the KYB
 * screen. Shared so both sides of the wire agree on the values.
 */
export const KYB_INDIVIDUAL_SOURCE = 'FrankieOneKYB';
export const KYB_ADDED_PARTY_SOURCE = 'PortalKYB';
export const KYB_MANUAL_INDIVIDUAL_SOURCE = 'PortalKYBManual';

/**
 * ApplicationIndividual.source marking an individual guarantor added from the KYC/AML tab's Add
 * Guarantor dialog. That dialog captures a name and date of birth only, so the row arrives needing
 * completion the same way a KYB-materialised one does - this is what says so after a reload.
 *
 * Deliberately outside isKybSourcedParty: the KYB saves full-sync the rows they own against their
 * own payload, and a row of theirs absent from it is soft-deleted. This row belongs to nobody's
 * payload, so it must read as legacy-owned exactly as an untagged one does.
 */
export const PORTAL_GUARANTOR_INDIVIDUAL_SOURCE = 'PortalGuarantor';

/**
 * ApplicationCommercialEntity.source marking the entity materialised from the application's
 * Previous Business ABN. Owned by the previous-business sync rather than the legacy parties payload,
 * which never carries it. Shared so the Additional Guarantors screen can block removal of the row.
 */
export const PREVIOUS_BUSINESS_ENTITY_SOURCE = 'PreviousBusiness';

/**
 * Whether a row was materialised by the KYB save rather than by a wizard step's parties payload.
 *
 * Readers of the legacy `Individuals` list need this because a key person ticked as a guarantor
 * carries `Role: 'Guarantor'` - their business role lives on a role row linked to their own entity,
 * which the legacy Role derivation only reads for the primary borrower - so they are otherwise
 * indistinguishable from an individual added on the Additional Guarantors step.
 */
export const isKybSourcedParty = (source?: string | null): boolean =>
  source === KYB_INDIVIDUAL_SOURCE
  || source === KYB_ADDED_PARTY_SOURCE
  || source === KYB_MANUAL_INDIVIDUAL_SOURCE;

/**
 * Whether a row of this source arrived knowing less than a whole individual - a ticked KYB
 * stakeholder, or an individual guarantor added from the KYC/AML tab. Both are flagged for
 * completion in the individuals table, and neither is issued a privacy consent form until it is
 * complete. Pre-KYB-go-live individuals who later get those details filled in are a separate
 * first-issue path, not this source check.
 */
export const isIncompleteOnArrivalSource = (source?: string | null): boolean =>
  source === KYB_INDIVIDUAL_SOURCE || source === PORTAL_GUARANTOR_INDIVIDUAL_SOURCE;

// Guarantor now lives on the individual, so it is supplied alongside the role's signing flag.
export const apiSignerRoleFromRoleFlags = (
  // Only isSigner is read, so the write path can pass that alone rather than build a whole role row.
  row: Pick<ApplicationIndividualRoleRow, 'isSigner'>,
  isGuarantor: boolean,
  linkedCommercialEntity?: ApplicationCommercialEntityRow,
): SignerRoleTypes => {
  if (isGuarantor && row.isSigner) return 'GuarantorSigner';
  if (isGuarantor && !row.isSigner) return 'Guarantor';
  if (row.isSigner) return 'Signer';
  return 'Others';
};

// ---------------------------------------------------------------------------
// Party integrity helpers (shared by frontend validators and backend guards)
//
// These enforce the rule that a borrowing entity (the primary applicant entity plus its
// trustees/partners, which borrow alongside it) must never also be a guarantor. Keeping the
// matching logic in one place means the submission form, the KYC additional-role panel and the
// backend persistence layer all agree on what "the same business" and "a borrower" mean.
// ---------------------------------------------------------------------------

// Borrowing commercial entity types: the primary applicant entity and its trustees/partners.
// Guarantors are not borrowers.
export const isBorrowerCommercialEntityType = (type: string | null | undefined): boolean =>
  type === 'Primary' || type === 'Trustee' || type === 'Partner';

/**
 * The single definition of "this entity borrows", for callers holding flags rather than a Type string:
 * the primary applicant entity, or a trustee or partner of a borrowing one. Never a guarantor —
 * assertNoBorrowerGuarantorConflict treats the overlap as an invariant violation.
 *
 * The guarantor term is load-bearing, not defensive. A trustee of a *guarantor* trust carries
 * isTrustee and isGuarantor together (collectAddedParties inherits the owner's side), so testing the
 * trustee/partner flags alone would report it as a borrower when it stands behind the guarantee.
 *
 * Both write paths and the API read projection go through this. They previously spelled the rule out
 * separately — `Type IN (Primary, Trustee, Partner)` on the legacy path, `!isGuarantor` on the KYB
 * one — which agreed only because KybAddedParty.kind is closed to 'Partner' | 'Trustee'.
 */
export const isBorrowerEntityFlags = (flags: {
  isPrimary?: boolean | null;
  isTrustee?: boolean | null;
  isPartner?: boolean | null;
  isGuarantor?: boolean | null;
}): boolean => !flags.isGuarantor && (!!flags.isPrimary || !!flags.isTrustee || !!flags.isPartner);

// Accepts either the lower-case business search shape (abn/acn) or the legacy commercial-entity
// JSON shape (ABN/ACN) so a single matcher works on both sides of the app.
export interface BusinessIdentifierLike {
  abn?: string | null;
  acn?: string | null;
  ABN?: string | null;
  ACN?: string | null;
}

const cleanBusinessIdentifier = (value: string | null | undefined): string =>
  (value ?? '').toString().replace(/\s/g, '').trim();

// Two businesses are the same when they share a non-empty ABN or a non-empty ACN.
export const businessIdentifiersMatch = (
  a: BusinessIdentifierLike | null | undefined,
  b: BusinessIdentifierLike | null | undefined,
): boolean => {
  if (!a || !b) return false;
  const abnA = cleanBusinessIdentifier(a.abn ?? a.ABN);
  const abnB = cleanBusinessIdentifier(b.abn ?? b.ABN);
  const acnA = cleanBusinessIdentifier(a.acn ?? a.ACN);
  const acnB = cleanBusinessIdentifier(b.acn ?? b.ACN);
  return (!!abnA && abnA === abnB) || (!!acnA && acnA === acnB);
};

export type CommercialEntityIntegrityErrorCode =
  | 'primaryEntityDuplicated'
  | 'borrowerAlsoGuarantor'
  | 'trusteeEntityTypeTrust'
  | 'partyEntityTypeSoleTrader';

export interface CommercialEntityIntegrityError {
  code: CommercialEntityIntegrityErrorCode;
  message: string;
  entityIndex: number;
}

export const PRIMARY_ENTITY_DUPLICATED_MESSAGE =
  'The applicant entity has also been entered as a trustee, partner or guarantor. Please remove the duplicate.';
export const TRUSTEE_ENTITY_TYPE_TRUST_MESSAGE =
  'The trustee cannot be an entity of type Trust. Please select the correct trustee entity.';
export const TRUSTEE_ENTITY_TYPE_SOLE_TRADER_MESSAGE =
  'A trustee cannot be a sole trader. Please enter the individual as the trustee instead.';
export const PARTNER_ENTITY_TYPE_SOLE_TRADER_MESSAGE =
  'A partner cannot be a sole trader. Please enter the individual as the partner instead.';

// One entity can break the same rule from several rows, so the same message must not be repeated
// when the errors are shown or joined into a single ClientError.
export const uniqueIntegrityMessages = (errors: { message: string }[]): string[] =>
  Array.from(new Set(errors.map(error => error.message)));

export interface KybPartyIntegrityInput extends BusinessIdentifierLike {
  role: 'trustee' | 'partner';
  // Accepts the UI form ('trust') or the stored code ('TRST').
  entityType?: string | null;
}

// A sole trader's ABN belongs to an individual, who must be entered as an Individual party instead.
// Shared by the KYB entry check and the full parties save so both reject it with the same message.
const soleTraderPartyMessage = (
  role: 'trustee' | 'partner',
  entityType: string | null | undefined,
): string | null => {
  if (entityTypeFromCode(entityType) !== 'sole-trader') return null;
  return role === 'trustee' ? TRUSTEE_ENTITY_TYPE_SOLE_TRADER_MESSAGE : PARTNER_ENTITY_TYPE_SOLE_TRADER_MESSAGE;
};

// Integrity rules for one trustee/partner entity entered on the KYB Key People step, checked at the
// point of entry rather than on the full parties save.
export const validateKybPartyIntegrity = (
  party: KybPartyIntegrityInput,
  primary: BusinessIdentifierLike | null | undefined,
): { code: CommercialEntityIntegrityErrorCode; message: string }[] => {
  const errors: { code: CommercialEntityIntegrityErrorCode; message: string }[] = [];
  if (primary && businessIdentifiersMatch(party, primary)) {
    errors.push({ code: 'primaryEntityDuplicated', message: PRIMARY_ENTITY_DUPLICATED_MESSAGE });
  }
  if (party.role === 'trustee' && entityTypeFromCode(party.entityType) === 'trust') {
    errors.push({ code: 'trusteeEntityTypeTrust', message: TRUSTEE_ENTITY_TYPE_TRUST_MESSAGE });
  }
  const soleTraderMessage = soleTraderPartyMessage(party.role, party.entityType);
  if (soleTraderMessage) {
    errors.push({ code: 'partyEntityTypeSoleTrader', message: soleTraderMessage });
  }
  return errors;
};

// Validate the commercial-entity list submitted for an application against the borrower/guarantor
// integrity rules. Returns one error per violated rule; an empty array means the set is valid.
//
// The relationship flags rather than the legacy Type string, via the same collapse the projection
// applies, so the rules read the shape the save actually receives. `Member`/`Secondary` are outside
// the collapse and fall to 'Guarantor', exactly as they already did on the legacy path.
export const validateCommercialEntitiesIntegrity = (
  commercialEntities: ApplicationCommercialEntityWrite[],
): CommercialEntityIntegrityError[] => {
  const errors: CommercialEntityIntegrityError[] = [];
  const active = commercialEntities
    .map((entity, index) => ({
      entity,
      index,
      roleType: commercialEntityRoleType({ ...entity, relationships: entity.relationships ?? [] }),
    }))
    .filter(({ entity }) => !entity.isDeleted);
  const primary = active.find(({ entity }) => entity.isPrimary);

  for (const { entity, index, roleType } of active) {
    if (entity.isPrimary) continue;

    // 1. The primary entity must not be re-entered as a trustee, partner or guarantor.
    if (primary && businessIdentifiersMatch(entity, primary.entity)) {
      errors.push({
        code: 'primaryEntityDuplicated',
        entityIndex: index,
        message: PRIMARY_ENTITY_DUPLICATED_MESSAGE,
      });
    }

    // 2. A trustee/partner (borrower) entity must not also be entered as a guarantor.
    if (roleType === 'Guarantor') {
      const borrowerMatch = active.find(({ entity: other, roleType: otherType }) =>
        !other.isPrimary &&
        isBorrowerCommercialEntityType(otherType) &&
        businessIdentifiersMatch(entity, other));
      if (borrowerMatch) {
        errors.push({
          code: 'borrowerAlsoGuarantor',
          entityIndex: index,
          message: 'A borrowing entity (trustee or partner) has also been entered as a guarantor. Please remove it from the guarantors.',
        });
      }
    }

    // 3. A trustee entity cannot itself be a trust (TRST).
    if (roleType === 'Trustee' && entity.entityType === 'TRST') {
      errors.push({
        code: 'trusteeEntityTypeTrust',
        entityIndex: index,
        message: TRUSTEE_ENTITY_TYPE_TRUST_MESSAGE,
      });
    }

    // 4. A trustee/partner entity cannot be a sole trader; the individual is entered instead.
    if (roleType === 'Trustee' || roleType === 'Partner') {
      const soleTraderMessage = soleTraderPartyMessage(roleType === 'Trustee' ? 'trustee' : 'partner', entity.entityType);
      if (soleTraderMessage) {
        errors.push({ code: 'partyEntityTypeSoleTrader', entityIndex: index, message: soleTraderMessage });
      }
    }
  }

  return errors;
};

const apiIndividualToRow = (
  applicationId: number,
  individual: ApplicationIndividualWrite,
  sortOrder: number,
  current: ApplicationIndividualRow | undefined,
  generateIndividualId?: () => string,
): ApplicationIndividualRow => {
  const legacyId = current?.legacyId ?? individual.guid ?? generateIndividualId?.() ?? '';
  return {
    applicationId,
    id: current?.id,
    legacyId,
    sortOrder,
    sfContactId: individual.sfContactId ?? null,
    title: individual.title ?? null,
    givenName: individual.givenName ?? null,
    middleName: individual.middleName ?? null,
    surname: individual.surname ?? null,
    dateOfBirth: individual.dateOfBirth ?? null,
    gender: individual.gender ?? null,
    email: individual.email ?? null,
    mobileNumber: individual.mobileNumber ?? null,
    homeAddress: apiAddressWriteToRow(applicationId, individual.homeAddress, current?.homeAddress),
    propertyOwnedAddress: apiAddressWriteToRow(applicationId, individual.property?.address, current?.propertyOwnedAddress),
    previousAddress: apiAddressWriteToRow(applicationId, individual.previousAddress, current?.previousAddress),
    // The nested-versus-flat precedence the legacy payload needed is resolved before this point, by
    // whichever adapter built the write shape.
    identificationType: individual.identification?.type ?? null,
    identificationNumber: individual.identification?.number ?? null,
    driverLicenseExpiry: individual.driverLicence?.expiry ?? null,
    driverLicenseState: individual.driverLicence?.state ?? null,
    hasAlias: individual.hasAlias ?? current?.hasAlias ?? null,
    aliasNames: (individual.hasAlias === false) ? null : (individual.aliasNames ?? current?.aliasNames ?? null),
    countryOfCitizenship: individual.countryOfCitizenship ?? current?.countryOfCitizenship ?? null,
    isGuarantor: individual.isGuarantor,
    isApplicant: individual.isApplicant,
    isPrimaryContact: individual.isPrimaryContact,
    propertyOwnerFlag: individual.property?.isOwner ?? null,
    isDeleted: individual.isDeleted ?? false,
    propertyValue: individual.property?.value ?? null,
    mortgageBalance: individual.property?.mortgageBalance ?? null,
    consumerData: individual.employment ?? null,
  };
};

const apiCommercialEntityToRow = (
  applicationId: number,
  entity: ApplicationCommercialEntityWrite,
  sortOrder: number,
  currentParties?: ApplicationPartiesSnapshot,
): ApplicationCommercialEntityRow => {
  const current = findCurrentCommercialEntity(currentParties?.commercialEntities ?? [], entity);
  const matchKey = current?.matchKey ?? commercialEntityMatchKeyFromApi(entity, sortOrder);
  // The single legacy Type string this entity would have carried. Still derived because apiType and
  // the entity-role rows below are expressed in those terms; it never reaches a column.
  const roleType = commercialEntityRoleType({
    isPrimary: entity.isPrimary,
    relationships: entity.relationships ?? [],
  });
  // Prefer real 4-digit class codes (explicit detail code, then the class field) over
  // SubdividionClass, which is only a last-resort fallback for dirty legacy data.
  const industryClassCode = firstString(
    entity.industry?.industryClassDetail?.['code'],
    entity.industry?.industryClass,
    entity.industry?.subdividionClass,
    // Carried separately by read-modify-write callers, whose `industry` block holds display names a
    // code cannot be recovered from.
    entity.industryClassCode,
  );
  // Legacy 3-digit group fallback (FK to IndustryGroup.code), only when there is no 4-digit class.
  const industryGroupCode = !industryClassCode && /^\d{3}$/.test(entity.industry?.industrySector ?? '')
    ? (entity.industry?.industrySector ?? null)
    : null;
  return {
    applicationId,
    id: current?.id,
    matchKey,
    apiType: roleType,
    sortOrder,
    legalName: entity.legalName ?? null,
    // Preserved rather than overwritten: the applicant form captures no trading name and so sends
    // none, and every column present on the row is written, so taking the payload at face value
    // would blank the stored one.
    tradingName: entity.tradingName || current?.tradingName || null,
    abn: entity.abn ?? null,
    acn: entity.acn ?? null,
    entityType: entity.entityType ?? null,
    isPrimary: entity.isPrimary,
    // Trustees and partners borrow alongside the primary applicant entity; guarantors do not, and
    // nor does a trustee of a guarantor trust, which carries isTrustee and isGuarantor together.
    isBorrower: isBorrowerEntityFlags({
      isPrimary: entity.isPrimary,
      isGuarantor: entity.isGuarantor,
      isTrustee: roleType === 'Trustee',
      isPartner: roleType === 'Partner',
    }),
    isGuarantor: entity.isGuarantor,
    manualEntry: entity.manualEntry ?? null,
    isDeleted: entity.isDeleted ?? false,
    abnAgeMonths: entity.abnAgeMonths ?? null,
    phoneNumber: entity.phoneNumber ?? null,
    areaCode: entity.areaCode ?? null,
    revenue: entity.revenue ?? null,
    operateAtCommercialAddressFlag: entity.operatesAtCommercialAddress ?? null,
    businessCategory: entity.businessCategory ?? null,
    principalPlaceOfBusinessAddress: apiAddressWriteToRow(applicationId, entity.principalPlaceOfBusiness, current?.principalPlaceOfBusinessAddress),
    registeredBusinessAddress: apiAddressWriteToRow(applicationId, entity.registeredBusinessAddress, current?.registeredBusinessAddress),
    // industryClassCode FKs to IndustryClass.code (the 4-digit ANZSIC class) and is what the read
    // path joins on to rebuild every industry field; industryGroupCode is the legacy 3-digit group
    // fallback (FK to IndustryGroup.code) for entities that never had a 4-digit class.
    industryClassCode,
    industryGroupCode,
    industryDetails: {
      primaryIndustry: entity.industry?.primaryIndustry ?? null,
      industrySector: entity.industry?.industrySector ?? null,
      industryClass: entity.industry?.industryClass ?? null,
      subdividionClass: entity.industry?.subdividionClass ?? null,
      industryDivision: entity.industry?.industryDivision ?? null,
      industryGroup: entity.industry?.industryGroup ?? null,
      industryClassDetail: entity.industry?.industryClassDetail ?? null,
    },
  };
};

const primaryIndividualRoleToRow = (
  applicationId: number,
  individualRow: ApplicationIndividualRow,
  individual: ApplicationIndividualWrite,
  primaryEntity: ApplicationCommercialEntityRow | undefined,
  sortOrder: number,
): ApplicationIndividualRoleRow | null => {
  const roleFlags = individual.role ?? roleFlagsFromApiRole(null);
  const signingFlags = { isSigner: individual.isSigner };
  // Applicant/primary-contact are per-individual (on ApplicationIndividual) and never warrant a role
  // row. A personal guarantor does, even though the flag itself also lives on the individual:
  // membership of the application is read off the presence of a live role row, so a guarantor
  // without one is taken for a party that was removed and has their unsigned consent form voided.
  // Anyone else carrying no entity-role flag and no signer status would be an empty row, so is skipped.
  if (!hasAnyRoleFlag(roleFlags) && !signingFlags.isSigner && !individualRow.isGuarantor) {
    return null;
  }
  return validateRoleRow({
    applicationId,
    individualId: individualRow.id,
    legacyIndividualId: individualRow.legacyId,
    commercialEntityId: primaryEntity?.id ?? null,
    commercialEntityMatchKey: primaryEntity?.matchKey,
    ...roleFlags,
    ...signingFlags,
    sortOrder,
    isDeleted: false,
  });
};

const additionalRolesToRows = (
  applicationId: number,
  individualRow: ApplicationIndividualRow,
  additionalRoles: ApplicationIndividualWrite['additionalRoles'],
  commercialEntityRows: ApplicationCommercialEntityRow[],
  currentRoles: ApplicationIndividualRoleRow[],
  sortOrderOffset: number,
): ApplicationIndividualRoleRow[] => {
  if (!additionalRoles?.length) return [];
  return additionalRoles.flatMap((role, index) => {
    const business = role.business;
    const businessABN = business?.abn ?? null;
    const businessACN = business?.acn ?? null;
    const businessName = business?.name ?? business?.displayName ?? null;
    const matchedEntity = findCommercialEntityByIdentifiers(commercialEntityRows, businessABN, businessACN, businessName);
    // An additional role is a link to an entity. Written without one it would land on the
    // individual's application-level row, and the role dedupe ORs its isSigner into that row.
    if (!matchedEntity) return [];
    // An entry that states its own flags is describing a relationship, not a guarantee, so the
    // guarantee this shape otherwise implies is not inferred from it. An entity that does guarantee
    // still arrives with isGuarantor set on its own payload entry.
    //
    // Nor is it inferred for a borrower-scope entity. A trustee or partner of the borrower reaches
    // this whenever a stored role against it is projected back through a payload - the read
    // direction has no other shape to put it in - and marking it a guarantor would make it both,
    // which assertNoBorrowerGuarantorConflict rejects on the way to the database.
    if (matchedEntity && !matchedEntity.isBorrower && !role.flags) matchedEntity.isGuarantor = true;
    // What the stored row records, for an entry that does not say. Carried forward rather than
    // written as false: a payload with no vocabulary for these flags must not be read as clearing
    // them - a shareholder of the corporate trustee, say.
    const storedRole = matchedEntity?.id != null && individualRow.id != null
      ? currentRoles.find(row => row.individualId === individualRow.id && row.commercialEntityId === matchedEntity.id)
      : undefined;
    return validateRoleRow({
      applicationId,
      individualId: individualRow.id,
      legacyIndividualId: individualRow.legacyId,
      commercialEntityId: matchedEntity?.id ?? null,
      commercialEntityMatchKey: matchedEntity?.matchKey,
      ...(role.flags ?? (storedRole ? {
        isDirector: storedRole.isDirector,
        isPartner: storedRole.isPartner,
        isShareholder: storedRole.isShareholder,
        isSoleTrader: storedRole.isSoleTrader,
        isTrustee: storedRole.isTrustee,
        isSpouse: storedRole.isSpouse,
      } : roleFlagsFromApiRole(null))),
      // Unstated falls back to the stored row before the default, for the same reason the flags do:
      // a KYB-recorded role that does not sign must not be made to sign by a payload echoing it
      // back. Only a genuinely new link defaults to signing, which is what one of these means.
      isSigner: role.isSigner ?? storedRole?.isSigner ?? true,
      sortOrder: sortOrderOffset + index,
      isDeleted: false,
    });
  });
};

const apiCommercialEntityRolesToRows = (
  applicationId: number,
  commercialEntities: ApplicationCommercialEntityWrite[],
  commercialEntityRows: ApplicationCommercialEntityRow[],
): ApplicationEntityRoleRow[] => {
  const borrower = commercialEntityRows.find(row => row.isPrimary);
  if (!borrower) return [];

  // Only trustee/partner are entity-to-entity edges; guarantor is an application-level flag
  // on the entity itself (isGuarantor) and has no owner-entity relationship.
  return commercialEntityRows
    .map((row, index) => ({ row, relationship: commercialEntities[index]?.relationships?.[0] }))
    .filter(({ row }) => row.matchKey !== borrower.matchKey)
    .filter(({ row }) => row.apiType === 'Trustee' || row.apiType === 'Partner')
    .map(({ row, relationship }) => {
      // A stated owner not among the rows being written falls back to the borrower rather than
      // leaving an edge that points at nothing.
      const owner = (relationship?.ownerEntityId != null
        ? commercialEntityRows.find(candidate => candidate.id === relationship.ownerEntityId && candidate !== row)
        : undefined) ?? borrower;
      return {
        applicationId,
        commercialEntityId: row.id,
        commercialEntityMatchKey: row.matchKey,
        ownerEntityId: owner.id,
        ownerEntityMatchKey: owner.matchKey,
        isTrustee: row.apiType === 'Trustee',
        isPartner: row.apiType === 'Partner',
        isDeleted: false,
      };
    });
};

const validateRoleRow = (row: ApplicationIndividualRoleRow): ApplicationIndividualRoleRow => {
  if (!row.individualId && !row.legacyIndividualId) {
    throw new Error('ApplicationIndividualRoleRow must have an individualId or legacyIndividualId');
  }
  return row;
};

const normalizeMatchName = (value: string | null | undefined): string | null => {
  const trimmed = value?.trim();
  return trimmed ? trimmed.toUpperCase() : null;
};

// ABN is a unique government identifier per legal entity, so an ABN match is trusted outright.
// ACN is not unique within an application: a trust has no ACN of its own and often records its
// corporate trustee's ACN, while that same company can separately appear as its own entity (e.g.
// guaranteeing in its own right) with an identical ACN. When more than one entity shares the ACN,
// the reference's business/organisation name is used to pick the matching entity by legalName; if
// that still can't disambiguate (or there's no name to compare), the match is left unresolved
// rather than guessing.
const findCommercialEntityByIdentifiers = (
  rows: ApplicationCommercialEntityRow[],
  abn: string | null,
  acn: string | null,
  name: string | null = null,
): ApplicationCommercialEntityRow | undefined => {
  if (abn) {
    const byAbn = rows.find(row => row.abn === abn);
    if (byAbn) return byAbn;
  }
  if (!acn) return undefined;
  const byAcn = rows.filter(row => row.acn === acn);
  if (byAcn.length <= 1) return byAcn[0];
  const normalizedName = normalizeMatchName(name);
  if (!normalizedName) return undefined;
  return byAcn.find(row => normalizeMatchName(row.legalName) === normalizedName);
};

const businessNumberLabel = (
  name: string | null | undefined,
  abn: string | null | undefined,
  acn: string | null | undefined,
): string => {
  if (abn) return `${name ?? ''} (ABN: ${abn})`;
  if (acn) return `${name ?? ''} (ACN: ${acn})`;
  return name ?? '';
};

const formattedBusinessNumber = (
  value: string | null | undefined,
  groups: number[],
  restoreLeadingZero = false,
): string => {
  const trimmed = value?.trim() ?? '';
  let digits = trimmed.replace(/\D/g, '');
  const expectedLength = groups.reduce((total, size) => total + size, 0);
  if (restoreLeadingZero && digits.length === expectedLength - 1) digits = `0${digits}`;
  if (digits.length !== expectedLength) return trimmed;
  let offset = 0;
  return groups.map(size => {
    const group = digits.slice(offset, offset + size);
    offset += size;
    return group;
  }).join(' ');
};

const trustNameForCapacityLabel = (value: string | null | undefined): string => {
  return (value ?? '')
    .trim()
    .toUpperCase()
    .replace(/^.*\b(?:IIOR|IHOR|ITOR)\s+&\s+ATF\s+/, '')
    .replace(/^THE TRUSTEE FOR\s+/, '');
};

/**
 * Formats an additional trust guarantor in its trustee capacity for legal documents.
 * Falls back to the existing business-number label when the trust or its trustees
 * cannot be resolved unambiguously from the application parties snapshot.
 */
export const formatAdditionalGuarantorBusiness = (
  business: BusinessNumberSearchValue,
  snapshot?: ApplicationPartiesSnapshot,
): string => {
  const fallback = businessNumberLabel(business?.name, business?.abn, business?.acn);
  if (!business || !snapshot) return fallback;

  const activeEntities = snapshot.commercialEntities.filter(entity => !entity.isDeleted);
  const matchedEntity = findCommercialEntityByIdentifiers(
    activeEntities,
    business.abn ?? null,
    business.acn ?? null,
    business.name,
  );
  if (!matchedEntity?.id) {
    return fallback;
  }

  const isTrust = (entity: ApplicationCommercialEntityRow): boolean =>
    ['trust', 'trst'].includes((entity.entityType ?? '').toLowerCase());
  const ownerTrusts = snapshot.entityRoles
    .filter(role =>
      !role.isDeleted &&
      role.isTrustee &&
      (role.commercialEntityId === matchedEntity.id ||
        (!!role.commercialEntityMatchKey && role.commercialEntityMatchKey === matchedEntity.matchKey)),
    )
    .map(role => activeEntities.find(entity =>
      entity.isGuarantor &&
      isTrust(entity) &&
      (entity.id === role.ownerEntityId ||
        (!!role.ownerEntityMatchKey && entity.matchKey === role.ownerEntityMatchKey)),
    ))
    .filter((entity): entity is ApplicationCommercialEntityRow => !!entity);
  const trust = isTrust(matchedEntity)
    ? matchedEntity
    : ownerTrusts.length === 1 ? ownerTrusts[0] : undefined;
  if (!trust?.id) return fallback;

  const isTrustRole = (
    commercialEntityId: number | null | undefined,
    commercialEntityMatchKey: string | undefined,
  ): boolean => {
    return commercialEntityId === trust.id ||
      (!!commercialEntityMatchKey && commercialEntityMatchKey === trust.matchKey);
  };

  const corporateTrustees = snapshot.entityRoles
    .filter(role =>
      !role.isDeleted &&
      role.isTrustee &&
      (role.ownerEntityId === trust.id ||
        (!!role.ownerEntityMatchKey && role.ownerEntityMatchKey === trust.matchKey)),
    )
    .map(role => activeEntities.find(entity =>
      entity.id === role.commercialEntityId ||
      (!!role.commercialEntityMatchKey && entity.matchKey === role.commercialEntityMatchKey),
    ))
    .filter((entity): entity is ApplicationCommercialEntityRow => !!entity)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const individualTrustees = snapshot.individualRoles
    .filter(role => !role.isDeleted && role.isTrustee && isTrustRole(role.commercialEntityId, role.commercialEntityMatchKey))
    .map(role => snapshot.individuals.find(individual =>
      !individual.isDeleted &&
      (individual.id === role.individualId || individual.legacyId === role.legacyIndividualId),
    ))
    .filter((individual): individual is ApplicationIndividualRow => !!individual)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const trustName = trustNameForCapacityLabel(trust.legalName);
  if (!trustName || (corporateTrustees.length > 0 && individualTrustees.length > 0) || corporateTrustees.length > 1) {
    return fallback;
  }

  const trustAbn = formattedBusinessNumber(trust.abn, [2, 3, 3, 3]);
  const trustLabel = trustAbn ? `${trustName} (ABN: ${trustAbn})` : trustName;

  if (corporateTrustees.length === 1) {
    const trustee = corporateTrustees[0];
    const trusteeName = trustee.legalName?.trim().toUpperCase();
    if (!trusteeName) return fallback;
    const trusteeAcn = formattedBusinessNumber(trustee.acn, [3, 3, 3], true);
    const trusteeLabel = trusteeAcn ? `${trusteeName} (ACN: ${trusteeAcn})` : trusteeName;
    return `${trusteeLabel} IIOR & ATF ${trustLabel}`;
  }

  const trusteeNames = individualTrustees
    .map(individual => [individual.givenName, individual.middleName, individual.surname]
      .filter(Boolean)
      .join(' ')
      .trim()
      .toUpperCase())
    .filter(Boolean);
  if (!trusteeNames.length) return fallback;

  const capacity = trusteeNames.length === 1 ? 'IHOR' : 'ITOR';
  return `${trusteeNames.join(' & ')} ${capacity} & ATF ${trustLabel}`;
};

const findCommercialEntityById = (
  rows: ApplicationCommercialEntityRow[],
  id?: number | null,
): ApplicationCommercialEntityRow | undefined => {
  return id ? rows.find(row => row.id === id) : undefined;
};

const findCommercialEntityByRole = (
  rows: ApplicationCommercialEntityRow[],
  role: ApplicationIndividualRoleRow,
): ApplicationCommercialEntityRow | undefined => {
  return findCommercialEntityById(rows, role.commercialEntityId) ??
    (role.commercialEntityMatchKey ? rows.find(row => row.matchKey === role.commercialEntityMatchKey) : undefined);
};

const normalizeDate = (value: string | Date | null | undefined): string | null | undefined => {
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  return value;
};

/**
 * The same rules as apiAddressToRow, reading the API shape - which is the address row itself, minus
 * the server-owned columns, so this is mostly a rename. An all-blank address is still treated as an
 * absent one, leaving whatever is stored alone rather than blanking it.
 */
const apiAddressWriteToRow = (
  applicationId: number,
  address: ApplicationAddressApi | null | undefined,
  current?: ApplicationAddressRow | null,
): ApplicationAddressRow | null => {
  if (!address) return current ? { ...current } : null;
  const hasAny = [
    address.unitNumber,
    address.streetNumber,
    address.streetName,
    address.streetType,
    address.suburb,
    address.state,
    address.postcode,
    address.unformattedAddress,
  ].some(value => value !== undefined && value !== null && `${value}`.trim() !== '');
  if (!hasAny) return current ? { ...current } : null;
  return {
    id: current?.id,
    applicationId,
    unitNumber: address.unitNumber ?? null,
    streetNumber: address.streetNumber ?? null,
    streetName: address.streetName ?? null,
    streetType: address.streetType ?? null,
    suburb: address.suburb ?? null,
    state: address.state ?? null,
    postcode: address.postcode ?? null,
    unformattedAddress: address.unformattedAddress ?? null,
    isManualInput: address.isManualInput ?? null,
    source: current?.source ?? 'Legacy',
  };
};

const parseConsumerData = (
  value: ApplicationConsumerData | string | null | undefined,
): ApplicationConsumerData | null => {
  if (!value) return null;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value) as ApplicationConsumerData;
    } catch {
      return null;
    }
  }
  return value;
};

const roleOrderKey = (role: ApplicationIndividualRoleRow): number => {
  return role.id ?? role.sortOrder ?? 0;
};

const findCurrentCommercialEntity = (
  rows: ApplicationCommercialEntityRow[],
  entity: ApplicationCommercialEntityWrite,
): ApplicationCommercialEntityRow | undefined => {
  // The primary entity is unique per application; match it by the isPrimary flag so a primary
  // entity that has no ABN/ACN still reconciles to its existing row instead of churning.
  if (entity.isPrimary) {
    return rows.find(row => row.isPrimary);
  }
  // Match by business identity (ABN/ACN) against all rows, including soft-deleted ones, so a
  // re-added entity revives its existing row. No positional fallback: matching the Nth posted
  // entity to the Nth row is unreliable across add/replace/re-add and produced orphaned
  // soft-deleted rows. An entity with no ABN/ACN match is treated as genuinely new.
  return findCommercialEntityByIdentifiers(rows, entity.abn ?? null, entity.acn ?? null, entity.legalName ?? null);
};

const commercialEntityMatchKeyFromApi = (
  entity: ApplicationCommercialEntityWrite,
  index: number,
): string => [
  entity.abn ?? '',
  entity.acn ?? '',
  entity.legalName ?? '',
  commercialEntityRoleType({ isPrimary: entity.isPrimary, relationships: entity.relationships ?? [] }),
  index,
].join('|');

// Resolve each individual in the payload to the stored row it describes, by id, then by identity, and
// only then by position. Neither the ids nor the length of this payload can be relied on: the wizard
// rebuilds it from the step forms on every save and mints a fresh id for each individual, and which
// individuals it covers depends on how the application was reached - a reloaded draft re-sends the KYB
// key people (the step 4 form is seeded from Application.Individuals), while a session that created
// the application sends only the guarantors added on top of them.
//
// Position is therefore the last resort, and only over the rows this payload owns: it is what tells a
// renamed individual from a new one, but applied to a row the payload does not describe it would write
// an added guarantor over a KYB key person.
const matchIndividualsToRows = (
  payload: ApplicationIndividualWrite[],
  currentRows: ApplicationIndividualRow[],
  isOwnedOutsidePayload: (row: ApplicationIndividualRow) => boolean,
): (ApplicationIndividualRow | undefined)[] => {
  const matches: (ApplicationIndividualRow | undefined)[] = payload.map(() => undefined);
  const claimed = new Set<ApplicationIndividualRow>();
  const claim = (index: number, row: ApplicationIndividualRow | undefined): void => {
    if (!row || claimed.has(row)) return;
    matches[index] = row;
    claimed.add(row);
  };

  payload.forEach((individual, index) => {
    if (individual.guid) claim(index, currentRows.find(row => row.legacyId === individual.guid));
  });

  const liveRows = currentRows.filter(row => !row.isDeleted);
  const openEntries = payload.map((_, index) => index).filter(index => !matches[index]);
  const openRows = liveRows.filter(row => !claimed.has(row));
  const identityOf = (person: ApplicationIndividualRow | ApplicationIndividualWrite) => ({
    givenName: person.givenName,
    middleName: person.middleName,
    surname: person.surname,
    dateOfBirth: dateOnly(person.dateOfBirth),
  });
  pairIndividualsByIdentity(openEntries.map(index => identityOf(payload[index])), openRows.map(identityOf))
    .rowFor.forEach((row, entry) => claim(openEntries[entry], openRows[row]));

  // An entry the identity pass left unpaired because several rows share its name keeps to those rows
  // in order, and is new if they are all taken: position must not hand it an unrelated person's row.
  const namesakeEntries = new Set<number>();
  openEntries.forEach(index => {
    if (matches[index]) return;
    const namesakes = liveRows.filter(row => compareIdentity(identityOf(payload[index]), identityOf(row)) === 'name-one-dob');
    if (!namesakes.length) return;
    namesakeEntries.add(index);
    claim(index, namesakes.find(row => !claimed.has(row)));
  });

  const unclaimedOwnedRows = liveRows.filter(row => !claimed.has(row) && !isOwnedOutsidePayload(row));
  let position = 0;
  payload.forEach((_individual, index) => {
    if (matches[index] || namesakeEntries.has(index)) return;
    claim(index, unclaimedOwnedRows[position++]);
  });

  return matches;
};

const dateOnly = (value: string | Date | null | undefined): string => {
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  return (value ?? '').toString().slice(0, 10);
};

// Hands out sort orders for individuals the payload adds, skipping every value already held - including
// by soft-deleted and KYB-owned rows, which keep their place in the list.
const sortOrderAllocator = (currentRows: ApplicationIndividualRow[]): (() => number) => {
  const taken = new Set(currentRows.map(row => row.sortOrder));
  let next = 0;
  return () => {
    while (taken.has(next)) next++;
    taken.add(next);
    return next;
  };
};

const firstString = (...values: unknown[]): string | null => {
  const value = values.find(item => typeof item === 'string' && item.trim());
  return typeof value === 'string' ? value : null;
};

const liveEntityRolesFor = (
  row: ApplicationCommercialEntityRow,
  roles: ApplicationEntityRoleRow[],
): ApplicationEntityRoleRow[] => roles.filter(item =>
  !item.isDeleted &&
  ((!!row.id && item.commercialEntityId === row.id) ||
    (!!row.matchKey && item.commercialEntityMatchKey === row.matchKey))
);

const roleBelongsToIndividual = (
  role: ApplicationIndividualRoleRow,
  individual: ApplicationIndividualRow,
): boolean => {
  return (!!individual.id && role.individualId === individual.id) ||
    (!!role.legacyIndividualId && role.legacyIndividualId === individual.legacyId);
};
